// Global ringing-call popup. Mounted once at the App level.
//
// Subscribes to the ringing WebSocket for the current user's domain (or all
// domains if master admin — we currently only show their own domain_id; ross
// can fall back to master via the View Portal flow). On every ringing_popup
// event we render a small toast-like card top-right. If no Smokeball contact
// was matched the card offers a "Create new" hint (clears after 30s).
import React, { useEffect, useReducer } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { PhoneIncoming, PhoneOutgoing, ExternalLink, X, UserPlus, Ban, ShieldOff, AlertOctagon, MessageSquare, Sparkles } from 'lucide-react';
import { useRingingSocket } from '../hooks/useRingingSocket';
import { useAuth } from '../contexts/AuthContext';

const AUTO_DISMISS_MS = 30000;

const visibleReducer = (state, action) => {
  switch (action.type) {
    case 'show':  return action.event;
    case 'hide':  return state?._id === action.id ? null : state;
    case 'clear': return null;
    default:      return state;
  }
};

export const RingingPopup = () => {
  const { user } = useAuth();
  const params = useParams();
  const location = useLocation();
  // Domain admins listen on their own domain. Master admins listen on the
  // domain they're currently browsing (extracted from /admin/domains/:id…
  // route param OR from the URL pathname so we don't need to mount the
  // popup inside every route).
  const routeDomain = params.domainId
    || (location.pathname.match(/\/admin\/domains\/([0-9a-f-]{36})/i) || [])[1];
  const domainId = user?.role === 'master_admin' ? routeDomain : user?.domain_id;
  const { event } = useRingingSocket(domainId);
  const [visible, dispatch] = useReducer(visibleReducer, null);

  useEffect(() => {
    if (!event) return undefined;
    dispatch({ type: 'show', event });
    const t = setTimeout(() => dispatch({ type: 'hide', id: event._id }), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [event]);

  if (!visible) return null;
  const Icon = visible.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming;
  const number = visible.direction === 'outbound' ? visible.to_number : visible.from_number;
  const contact = visible.contact;
  const gymsales = visible.gymsales;
  const gymsalesMatched = gymsales && gymsales.matched;
  const hint = visible.playbook_hint;

  return (
    <div
      className="fixed top-6 right-6 z-[9999] w-80 bg-white shadow-2xl rounded-xl border border-slate-200 p-4 animate-in slide-in-from-right-4"
      data-testid="ringing-popup"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center">
            <Icon className="w-4 h-4 text-teal-600" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">
              {visible.direction === 'outbound' ? 'Calling' : 'Incoming'}
            </p>
            <p className="font-mono text-sm text-slate-700">{number || '(unknown)'}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => dispatch({ type: 'clear' })}
          className="text-slate-400 hover:text-slate-700"
          data-testid="ringing-popup-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {contact ? (
        <div className="border-t border-slate-100 pt-3" data-testid="ringing-popup-matched">
          <p className="text-xs uppercase text-slate-400">Smokeball Contact</p>
          <p className="text-base font-semibold text-slate-900 mt-1">{contact.name || 'Unnamed contact'}</p>
          <a
            href={contact.deep_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-sm text-teal-600 hover:text-teal-700"
            data-testid="ringing-popup-deep-link"
          >
            Open in Smokeball <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      ) : gymsalesMatched ? (
        <div className="border-t border-slate-100 pt-3" data-testid="ringing-popup-gymsales-matched">
          <p className="text-xs uppercase text-emerald-600 font-semibold">GymSales Match</p>
          <p className="text-base font-semibold text-slate-900 mt-1">
            {[gymsales.first_name, gymsales.last_name].filter(Boolean).join(' ') || 'Unnamed lead'}
          </p>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
            {gymsales.status && (
              <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-medium">{gymsales.status}</span>
            )}
            {gymsales.assigned_to && (
              <span className="text-slate-500">→ {gymsales.assigned_to}</span>
            )}
          </div>
          {(gymsales.do_not_call || gymsales.sms_opted_out || gymsales.email_opted_out || gymsales.email_suppressed) && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {gymsales.do_not_call && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold" title="Do Not Call">
                  <Ban className="w-2.5 h-2.5" />DNC
                </span>
              )}
              {gymsales.sms_opted_out && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold" title="SMS Opt-out">
                  <ShieldOff className="w-2.5 h-2.5" />SMS Opt-out
                </span>
              )}
              {gymsales.email_opted_out && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold" title="Email Opt-out">
                  <ShieldOff className="w-2.5 h-2.5" />Email Opt-out
                </span>
              )}
              {gymsales.email_suppressed && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-bold" title="Email Suppressed">
                  <AlertOctagon className="w-2.5 h-2.5" />Email Suppressed
                </span>
              )}
            </div>
          )}
          {Array.isArray(gymsales.tags) && gymsales.tags.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {gymsales.tags.slice(0, 4).map((t) => (
                <span key={t} className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[10px]">{t}</span>
              ))}
            </div>
          )}
          {gymsales.deep_link && (
            <a
              href={gymsales.deep_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm text-emerald-600 hover:text-emerald-700"
              data-testid="ringing-popup-gymsales-deep-link"
            >
              Open in GymSales <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      ) : (
        <div className="border-t border-slate-100 pt-3" data-testid="ringing-popup-unmatched">
          <p className="text-xs uppercase text-slate-400">
            {gymsales ? 'No match in GymSales' : 'No match in Smokeball'}
          </p>
          {!gymsales && (
            <button
              type="button"
              className="inline-flex items-center gap-1 mt-2 text-sm text-slate-600 hover:text-slate-900"
              onClick={() => window.open('https://rc-app.smokeball.com.au', '_blank', 'noopener')}
              data-testid="ringing-popup-create-new"
            >
              <UserPlus className="w-3.5 h-3.5" /> Create new in Smokeball →
            </button>
          )}
          {gymsales && (
            <button
              type="button"
              className="inline-flex items-center gap-1 mt-2 text-sm text-slate-600 hover:text-slate-900"
              onClick={() => window.open('https://login.gymsales.net/', '_blank', 'noopener')}
              data-testid="ringing-popup-gymsales-create-new"
            >
              <UserPlus className="w-3.5 h-3.5" /> Open GymSales →
            </button>
          )}
          <p className="text-[10px] text-slate-400 mt-1.5">Auto-dismisses in 30 seconds.</p>
        </div>
      )}

      {hint && (hint.opening_line || (hint.objections && hint.objections.length > 0)) && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid="ringing-popup-playbook-hint">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-teal-600" />
            <span className="text-[10px] uppercase tracking-widest font-semibold text-teal-700">Playbook · {hint.archetype}</span>
          </div>
          {hint.opening_line && (
            <blockquote className="border-l-2 border-teal-500 pl-2.5 py-0.5 text-xs italic text-slate-800 mb-2" data-testid="popup-opening-line">
              &ldquo;{hint.opening_line}&rdquo;
            </blockquote>
          )}
          {hint.objections?.length > 0 && (
            <div className="space-y-1.5" data-testid="popup-objections">
              {hint.objections.map((o, i) => (
                <div key={i} className="bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  <div className="flex items-start gap-1.5">
                    <MessageSquare className="w-3 h-3 text-amber-700 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-amber-800 text-[10px] font-mono truncate">{o.objection}</div>
                      <div className="text-slate-700 text-[11px] mt-0.5">→ {o.response}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
